import React, { useState } from "react";
import { MessageSquarePlus, Check } from "lucide-react";
import { api } from "@/platform/api";
import { Button } from "@/platform/components/primitives";

const SWATCHES = ["#FFD400", "#4F46E5", "#0079BF", "#00C2E0", "#61BD4F", "#FF9F1A", "#EB5A46", "#C377E0", "#111111"];

const inkFor = (hex) => {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#111111" : "#ffffff";
  } catch (_e) { return "#111111"; }
};

export default function WidgetSettings({ project, onSaved }) {
  const [label, setLabel] = useState(project.widgetLabel || "Review this page");
  const [color, setColor] = useState((project.widgetColor || "#FFD400").toUpperCase());
  const [position, setPosition] = useState(project.widgetPosition || "bottom-right");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const valid = /^#([0-9a-fA-F]{6})$/.test(color);

  const save = async () => {
    if (!valid) { setError("Enter a valid #RRGGBB colour"); return; }
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await api.patch(`/api/projects/${project.id}/appearance`, {
        widgetLabel: label.trim() || "Review this page",
        widgetColor: color.toUpperCase(),
        widgetPosition: position,
      });
      setSaved(true); setTimeout(() => setSaved(false), 2200);
      onSaved?.(res);
    } catch (_e) { setError("Could not save. Please try again."); }
    setSaving(false);
  };

  const ink = inkFor(valid ? color : "#FFD400");

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_380px]" data-testid="widget-settings">
      <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-7">
        <h2 className="text-[18px] font-semibold text-[#F4F5F8]">Widget appearance</h2>
        <p className="mt-1 text-[14px] text-[#98A0B0]">Customise the launcher your reviewers see on {project.name}. This styles the launcher button (label + colour) — it is <span className="text-[#C4C9D4]">not</span> your reviewer name/identity. Changes apply the next time the page loads.</p>

        <div className="mt-6 space-y-6">
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Launcher label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 40))}
              placeholder="Review this page"
              className="w-full rounded-xl border border-[#2A3040] bg-[#0E1016] px-3.5 py-2.5 text-[14px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]"
              data-testid="widget-label-input"
            />
            <p className="mt-1 text-[11px] text-[#565E70]">{label.length}/40</p>
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Launcher colour</label>
            <div className="flex flex-wrap items-center gap-2">
              {SWATCHES.map((s) => (
                <button
                  key={s}
                  onClick={() => setColor(s)}
                  className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color.toUpperCase() === s ? "border-white" : "border-transparent"}`}
                  style={{ background: s }}
                  aria-label={s}
                  data-testid={`widget-swatch-${s.slice(1)}`}
                />
              ))}
              <div className="ml-1 flex items-center gap-2">
                <input type="color" value={valid ? color : "#FFD400"} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-8 w-8 cursor-pointer rounded-md border border-[#2A3040] bg-transparent p-0.5" data-testid="widget-color-native" />
                <input
                  value={color}
                  onChange={(e) => setColor(e.target.value.toUpperCase())}
                  className="w-[104px] rounded-lg border border-[#2A3040] bg-[#0E1016] px-2.5 py-1.5 font-mono text-[12.5px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]"
                  data-testid="widget-color-hex"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Position</label>
            <div className="inline-flex rounded-xl border border-[#242A38] bg-[#0E1016] p-1">
              {[["bottom-left", "Bottom left"], ["bottom-right", "Bottom right"]].map(([val, lbl]) => (
                <button
                  key={val}
                  onClick={() => setPosition(val)}
                  className={`rounded-lg px-4 py-2 text-[13px] font-medium transition-colors ${position === val ? "bg-[#1C2130] text-[#F4F5F8]" : "text-[#6E7686] hover:text-[#AAB1BF]"}`}
                  data-testid={`widget-position-${val}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={save} loading={saving} className="sm:w-auto sm:px-6" data-testid="widget-save-btn">Save changes</Button>
            {saved && <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#4ADE80]" data-testid="widget-saved"><Check className="h-4 w-4" /> Saved</span>}
            {error && <span className="text-[13px] text-[#F87171]" data-testid="widget-error">{error}</span>}
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div>
        <p className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Live preview</p>
        <div className="relative h-[320px] overflow-hidden rounded-2xl border border-[#242A38] bg-gradient-to-br from-[#1a1f2e] to-[#0E1016]" data-testid="widget-preview">
          <div className="p-5">
            <div className="h-3 w-24 rounded bg-[#2A3040]" />
            <div className="mt-3 h-2 w-full rounded bg-[#20263400]" />
            <div className="mt-6 space-y-2">
              <div className="h-2.5 w-3/4 rounded bg-[#232a3a]" />
              <div className="h-2.5 w-2/3 rounded bg-[#232a3a]" />
              <div className="h-2.5 w-1/2 rounded bg-[#232a3a]" />
            </div>
          </div>
          <div
            className={`absolute bottom-4 flex items-center gap-2 rounded-full px-3 py-2 shadow-[0_16px_48px_rgba(0,0,0,0.45)] ${position === "bottom-left" ? "left-4" : "right-4"}`}
            style={{ background: valid ? color : "#FFD400", color: ink }}
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full"><MessageSquarePlus size={15} strokeWidth={2.5} /></span>
            <span className="pr-1 text-[13px] font-semibold tracking-[0.02em]">{label || "Review this page"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
